<script setup lang="ts">
import { Head } from '@inertiajs/vue3';
import { WebsiteRenderer } from '@proexpert/website-renderer';
import type {
    WebsiteBlueprint,
    WebsiteFormCaptureState,
    WebsiteFormCompletion,
    WebsiteFormSubmission,
    WebsiteLeadCaptureState,
    WebsiteLeadSubmission,
} from '@proexpert/website-renderer';
import { computed, reactive, shallowRef } from 'vue';
import StorePublicLeadController from '@/actions/App/Http/Controllers/StorePublicLeadController';
import StoreWebsiteFormSubmissionController from '@/actions/App/Http/Controllers/StoreWebsiteFormSubmissionController';
import { localHref } from '@/lib/localHref';

const props = defineProps<{
    blueprint: WebsiteBlueprint;
    pageTitle: string;
    pageDescription: string;
    pagePath: string;
    canonicalUrl: string;
    structuredData: Record<string, unknown> | null;
}>();

const leadCapture = reactive<WebsiteLeadCaptureState>({
    enabled: true,
    submitting: false,
    succeeded: false,
});
const formCapture = reactive<WebsiteFormCaptureState>({
    enabled: true,
    submitting: false,
    succeeded: false,
});
const submissionKey = shallowRef(createSubmissionKey());
const structuredDataJson = computed(() => props.structuredData === null
    ? ''
    : JSON.stringify(props.structuredData).replaceAll('<', '\\u003c'));
const websiteFontLinks = computed(() => [props.blueprint.typography?.heading, props.blueprint.typography?.body, props.blueprint.typography?.wordmark]
    .filter((font, index, fonts) => Boolean(font?.provider === 'bunny' && font.urlString)
        && fonts.findIndex((candidate) => candidate?.key === font?.key) === index)
    .map((font) => `https://fonts.bunny.net/css?family=${font!.urlString}`));

function createSubmissionKey(): string {
    if (typeof window === 'undefined') {
        return '00000000-0000-4000-8000-000000000000';
    }

    if (typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

async function submitLead(submission: WebsiteLeadSubmission): Promise<void> {
    if (leadCapture.submitting) {
        return;
    }

    leadCapture.submitting = true;
    leadCapture.succeeded = false;
    leadCapture.message = undefined;
    leadCapture.errors = undefined;

    try {
        const endpoint = StorePublicLeadController();
        const response = await fetch(localHref(endpoint.url), {
            method: endpoint.method.toUpperCase(),
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({
                ...submission,
                submissionKey: submissionKey.value,
                sourcePagePath: props.pagePath,
                challengeToken: null,
            }),
        });
        const payload = await response.json() as {
            message?: string;
            errors?: Record<string, string[]>;
        };

        if (!response.ok) {
            leadCapture.errors = Object.fromEntries(
                Object.entries(payload.errors ?? {}).map(([field, messages]) => [field, messages[0]]),
            );
            leadCapture.message = response.status === 429
                ? 'Please wait a moment before trying again.'
                : (payload.message ?? 'We could not send your message. Please try again.');

            return;
        }

        leadCapture.succeeded = true;
        leadCapture.message = payload.message ?? 'Thanks. Your message has been sent.';
        submissionKey.value = createSubmissionKey();
    } catch {
        leadCapture.message = 'We could not send your message. Please check your connection and try again.';
    } finally {
        leadCapture.submitting = false;
    }
}

async function submitForm(submission: WebsiteFormSubmission): Promise<void> {
    if (formCapture.submitting) {
        return;
    }

    formCapture.submitting = true;
    formCapture.succeeded = false;
    formCapture.message = undefined;
    formCapture.errors = undefined;

    try {
        const endpoint = StoreWebsiteFormSubmissionController({
            form: submission.formId,
        });
        const response = await fetch(localHref(endpoint.url), {
            method: endpoint.method.toUpperCase(),
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({
                ...submission,
                submissionKey: submissionKey.value,
                sourcePagePath: props.pagePath,
                challengeToken: null,
            }),
        });
        const payload = await response.json() as {
            message?: string;
            errors?: Record<string, string[]>;
            completion?: WebsiteFormCompletion | null;
        };

        if (!response.ok) {
            formCapture.errors = Object.fromEntries(
                Object.entries(payload.errors ?? {}).map(([field, messages]) => [field, messages[0]]),
            );
            formCapture.message = response.status === 429
                ? 'Please wait a moment before trying again.'
                : (payload.message ?? 'We could not send your form. Please try again.');

            return;
        }

        formCapture.succeeded = true;
        formCapture.message = payload.message ?? 'Thanks. Your submission has been received.';
        formCapture.completion = payload.completion ?? null;
        submissionKey.value = createSubmissionKey();

        const completion = payload.completion;
        if (completion?.type === 'redirect') {
            window.location.assign(completion.url);
        } else if (completion?.type === 'resource') {
            window.open(completion.url, '_blank', 'noopener,noreferrer');
        }
    } catch {
        formCapture.message = 'We could not send your form. Please check your connection and try again.';
    } finally {
        formCapture.submitting = false;
    }
}
</script>

<template>
    <Head :title="pageTitle">
        <link rel="canonical" :href="canonicalUrl" />
        <link v-for="href in websiteFontLinks" :key="href" rel="stylesheet" :href="href" />
        <link v-if="props.blueprint.seo?.faviconUrl" rel="icon" :href="props.blueprint.seo.faviconUrl" />
        <meta name="description" :content="pageDescription" />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" :content="pageTitle" />
        <meta property="og:description" :content="pageDescription" />
        <meta property="og:url" :content="canonicalUrl" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" :content="blueprint.name" />
        <meta v-if="props.blueprint.seo?.openGraphImageUrl" property="og:image" :content="props.blueprint.seo.openGraphImageUrl" />
        <meta v-if="props.blueprint.seo?.openGraphImageUrl" property="og:image:alt" :content="`${blueprint.name} social preview`" />
        <meta name="twitter:card" :content="props.blueprint.seo?.openGraphImageUrl ? 'summary_large_image' : 'summary'" />
        <meta name="twitter:title" :content="pageTitle" />
        <meta name="twitter:description" :content="pageDescription" />
        <meta v-if="props.blueprint.seo?.openGraphImageUrl" name="twitter:image" :content="props.blueprint.seo.openGraphImageUrl" />
        <component
            :is="'script'"
            v-if="structuredData"
            type="application/ld+json"
            v-text="structuredDataJson"
        />
    </Head>

    <main class="min-h-dvh bg-white">
        <WebsiteRenderer
            :blueprint="blueprint"
            :page-slug="pagePath"
            :lead-capture="leadCapture"
            :form-capture="formCapture"
            @lead-submit="submitLead"
            @form-submit="submitForm"
        />
    </main>
</template>
